/**
 * Приход и уход одним полем «ЧЧ:ММ – ЧЧ:ММ», набором цифр (просьба
 * владельца 20.10.2026).
 *
 * Два поля `type="time"` на телефоне открывали колёсико или циферблат, и
 * пятнадцать человек вечером крутили их по одному. Здесь человек набирает
 * подряд восемь цифр на цифровой клавиатуре — 0 9 2 0 2 3 2 0, — а двоеточие
 * и тире встают сами, и курсор перескакивает через них. Серая подсказка
 * в том же поле показывает, что осталось набрать.
 *
 * Строгости нет (владелец 20.10.2026: «не приходилось вводить 0920, а
 * просто 920»): однозначный час дополняется нулём сам, точка или двоеточие
 * после одной цифры часа — тоже («1.30» → 01:30), а недописанное время при
 * уходе из поля дописывается нулями — «9» → 09:00, «09:3» → 09:30. Правило
 * одно: записывается то, что видно в поле, с нулями на пустых местах.
 * Поэтому уход в 0:30 набирают строго «0030»: «030» — это 03:0 → 03:00.
 * Угадывать здесь не нужно — правило подтвердил владелец 20.10.2026.
 *
 * Курсор всегда в конце: исправляют кнопкой ⌫, а не тычком в середину —
 * у маски с произвольным курсором на телефоне слишком много способов
 * сломаться. Только приход — человек ещё на смене.
 */

import { useEffect, useRef, useState } from 'react';

export const TEMPLATE = 'ЧЧ:ММ – ЧЧ:ММ';

/**
 * Добавить одну цифру к набранным. Невозможная цифра не принимается:
 * «24» часа, «60» минут. Первая цифра часа больше двух — значит, час
 * однозначный: «9» превращается в «09», и курсор уходит к минутам.
 */
function feed(digits: string, digit: string): string {
  if (digits.length >= 8) return digits;
  const place = digits.length % 4;
  const value = Number(digit);
  if (place === 0) return value > 2 ? `${digits}0${digit}` : digits + digit;
  if (place === 1 && digits[digits.length - 1] === '2' && value > 3) return digits;
  if (place === 2 && value > 5) return digits;
  return digits + digit;
}

/**
 * Цифры из того, что оказалось в поле: набрано, стёрто, вставлено.
 * Разделитель после одной цифры часа значит, что час однозначный: «1:» → «01».
 * В показанном поле разделитель после одной цифры не стоит никогда, так что
 * повторный разбор показанного ничего не меняет.
 */
export function typedDigits(raw: string): string {
  let digits = '';
  for (const char of raw) {
    if (char >= '0' && char <= '9') digits = feed(digits, char);
    else if (digits.length % 4 === 1) digits = `${digits.slice(0, -1)}0${digits.slice(-1)}`;
  }
  return digits;
}

/** «0920» → «09:20», «09202» → «09:20 – 2». Разделитель — только перед цифрой. */
export function formatDigits(digits: string): string {
  let text = '';
  for (let i = 0; i < digits.length; i += 1) {
    if (i === 2 || i === 6) text += ':';
    if (i === 4) text += ' – ';
    text += digits[i];
  }
  return text;
}

/** «09:20» и «23:20» → «09202320». */
export function digitsOf(started: string, finished: string): string {
  return typedDigits(started) + (started ? typedDigits(finished) : '');
}

/** Недописанное время — нулями на пустых местах: «1» → 01:00, «093» → 09:30. */
export function padTime(part: string): string {
  const hours = part.length === 1 ? `0${part}` : part.slice(0, 2);
  return `${hours}:${part.slice(2, 4).padEnd(2, '0')}`;
}

/** Цифры поля → приход и уход; ухода нет — пустая строка. */
export function timesOf(digits: string): [string, string] {
  const finished = digits.slice(4);
  return [padTime(digits.slice(0, 4)), finished ? padTime(finished) : ''];
}

export function TimeRangeField({
  started,
  finished,
  disabled,
  autoFocus,
  onCommit,
  onEmpty,
}: {
  started: string;
  finished: string;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Дописано: приход, и уход — если набран. */
  onCommit: (started: string, finished: string) => void;
  /** Поле стёрли целиком. */
  onEmpty?: () => void;
}) {
  const [digits, setDigits] = useState(() => digitsOf(started, finished));
  const input = useRef<HTMLInputElement>(null);
  const typed = useRef(false);

  useEffect(() => {
    setDigits(digitsOf(started, finished));
  }, [started, finished]);

  const text = formatDigits(digits);
  const toEnd = () => {
    const field = input.current;
    if (field && document.activeElement === field) {
      field.setSelectionRange(field.value.length, field.value.length);
    }
  };
  useEffect(() => {
    toEnd();
    // Набрали уход до конца — смена записана, клавиатура убирается сама.
    if (typed.current && digits.length === 8) input.current?.blur();
    typed.current = false;
  }, [digits]);

  const finish = () => {
    if (digits.length === 0) {
      onEmpty?.();
      return;
    }
    const [nextStarted, nextFinished] = timesOf(digits);
    setDigits(digitsOf(nextStarted, nextFinished));
    onCommit(nextStarted, nextFinished);
  };

  return (
    <div className="ts-range">
      <div className="ts-range__box">
        <span className="ts-range__ghost" aria-hidden="true">
          <span className="ts-range__typed">{text}</span>
          {TEMPLATE.slice(text.length)}
        </span>
        <input
          ref={input}
          className="input ts-range__input"
          type="text"
          inputMode="numeric"
          enterKeyHint="done"
          autoComplete="off"
          aria-label="приход и уход"
          autoFocus={autoFocus}
          disabled={disabled}
          value={text}
          onFocus={() => requestAnimationFrame(toEnd)}
          onClick={toEnd}
          onKeyDown={(event) => {
            if (event.key === 'Enter') input.current?.blur();
          }}
          onChange={(event) => {
            typed.current = true;
            setDigits(typedDigits(event.target.value));
          }}
          onBlur={finish}
        />
      </div>
    </div>
  );
}
